/**
 * Persists the last known signed-in / subscription state for instant homepage
 * routing on return visits. New visitors have no entry and see the guest
 * landing immediately without waiting on Firebase Auth or weekly drops.
 */

export type HomeSessionCache = {
  signedIn: boolean;
  isSubscriber: boolean;
};

const STORAGE_KEY = "rp.homeSession.v1";
const JUST_SUBSCRIBED_KEY = "rp.justSubscribed.v1";
const JUST_SUBSCRIBED_TTL_MS = 60_000;

function isHomeSessionCache(value: unknown): value is HomeSessionCache {
  if (!value || typeof value !== "object") return false;
  const v = value as HomeSessionCache;
  return typeof v.signedIn === "boolean" && typeof v.isSubscriber === "boolean";
}

/** Synchronous read for first paint (client-only). */
export function readHomeSessionCache(): HomeSessionCache | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isHomeSessionCache(parsed)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

export function writeHomeSessionCache(cache: HomeSessionCache): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearHomeSessionCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Optimistic flag set immediately after checkout so /library avoids a paywall flash. */
export function markJustSubscribed(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(JUST_SUBSCRIBED_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function readJustSubscribed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(JUST_SUBSCRIBED_KEY);
    if (!raw) return false;
    const ts = Number.parseInt(raw, 10);
    if (!Number.isFinite(ts) || Date.now() - ts > JUST_SUBSCRIBED_TTL_MS) {
      window.sessionStorage.removeItem(JUST_SUBSCRIBED_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearJustSubscribed(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(JUST_SUBSCRIBED_KEY);
  } catch {
    /* ignore */
  }
}
